import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Users, GraduationCap, TrendingUp, Bell, ArrowLeft } from "lucide-react";

const TABS = [
  { to: "/principal", end: true, label: "Overview", icon: LayoutDashboard },
  { to: "/principal/teachers", label: "Teachers", icon: Users },
  { to: "/principal/students", label: "Students", icon: GraduationCap },
  { to: "/principal/progress", label: "Progress", icon: TrendingUp },
  { to: "/principal/notices", label: "Notices", icon: Bell },
];

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export default function PrincipalShell() {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // school_id is resolved server-side for a principal (her own
        // school); this call also gives the id notices needs.
        const { data } = await client.get("/principal/overview");
        if (cancelled) return;
        const sid = data?.school?.id;
        if (!sid) return;
        const { data: notices } = await client.get("/school/notices", { params: { school_id: sid } });
        if (!cancelled) setUnread((notices.notices || []).filter((n) => !n.is_read).length);
      } catch {
        /* ignore — badge just stays at 0 */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const firstName = (user?.name || "").split(" ")[0] || "there";

  return (
    <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8" data-testid="principal-shell">
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Same look as the Little Verses (Poems) principal bar: a muted back link, and a white pill with a bright-green active tab. */}
        <Link to="/dashboard" className="inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-heading font-semibold text-[#5F6B60] hover:bg-white hover:text-[#2C5F3B] transition-colors duration-200">
          <ArrowLeft size={16} strokeWidth={2.4} className="shrink-0" /> Back to dashboard
        </Link>

        <nav className="flex items-center gap-1 rounded-full bg-white border border-[#D5F2E0] p-1 max-w-full overflow-x-auto" data-testid="principal-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                data-testid={`principal-tab-${t.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-heading font-semibold transition-colors ${
                    isActive ? "bg-[#4ECB7E] text-white" : "text-[#5F6B60] hover:bg-[#F5FDF8]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon size={14} strokeWidth={2.6} className="shrink-0" />
                    {t.label}
                    {t.label === "Notices" && unread > 0 && (
                      <span className={`ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-bold ${isActive ? "bg-white text-[#4ECB7E]" : "bg-[#2E9E4F] text-white"}`} data-testid="notices-unread-badge">
                        {unread}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>
      </div>

      <div className="mt-7">
        <span className="hv-badge">Principal</span>
        <h1 className="hv-heading mt-3 text-3xl sm:text-4xl">
          <span className="text-hv-navy">{greeting()},</span> <span className="text-hv-coral">{firstName}</span>
        </h1>
        <p className="mt-1.5 text-hv-slate text-sm">Here&apos;s how your school is progressing today.</p>
      </div>

      <div className="mt-8">
        <Outlet />
      </div>
    </main>
  );
}
